import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { SourcePostDto } from '@mpr/shared';

import { api, ApiError } from '../lib/api.js';
import type { TranslationKey } from '../i18n/translations.js';
import { DestinationFeature } from '@mpr/shared';
import { DestinationFilter } from './DestinationPicker.js';
import { pageIdOf } from '../lib/destinations.js';
import { withChannel } from '../lib/channels.js';
import type { Selection } from '../App.js';
import type { StudioHandoff } from '../lib/studioProject.js';
import { ErrorNotice } from './ErrorNotice.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import { ComposerForm } from './ComposerForm.js';
import { Icon } from './Icons.js';
import { PostCard } from './PostCard.js';

type Mode = 'owned' | 'public' | 'compose';

export function SourcePanel({
  onSelect,
  handoff,
}: {
  onSelect: (selection: Selection) => void;
  /** Threaded straight to the compose tab — see ComposerForm. */
  handoff?: StudioHandoff;
}) {
  const [mode, setMode] = useState<Mode>(handoff ? 'compose' : 'owned');
  const { t } = useI18n();

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('source.title')}</h2>
        <div className="segmented">
          <button
            type="button"
            className={mode === 'owned' ? 'active' : ''}
            onClick={() => setMode('owned')}
          >
            {t('source.mode.owned')}
          </button>
          <button
            type="button"
            className={mode === 'public' ? 'active' : ''}
            onClick={() => setMode('public')}
          >
            {t('source.mode.public')}
          </button>
          <button
            type="button"
            className={mode === 'compose' ? 'active' : ''}
            onClick={() => setMode('compose')}
          >
            {t('source.mode.compose')}
          </button>
        </div>
      </div>

      {mode === 'owned' && <OwnedSource onSelect={onSelect} />}
      {mode === 'public' && <PublicSource onSelect={onSelect} />}
      {mode === 'compose' && <ComposerForm onSelect={onSelect} handoff={handoff} />}
    </section>
  );
}

function OwnedSource({ onSelect }: { onSelect: (selection: Selection) => void }) {
  /** A destination id: a Page (`page:…`) or a social account (`soc:…`). */
  const [destination, setDestination] = useState('');
  const { t } = useI18n();

  const pageId = pageIdOf(destination);
  const socialId = destination.startsWith('soc:') ? destination.slice(4) : '';

  const posts = useQuery({
    queryKey: ['owned-posts', pageId],
    queryFn: () => api.ownedPosts(pageId),
    enabled: pageId !== '',
  });

  return (
    <>
      <label htmlFor="source-page">{t('source.selectPage')}</label>
      <DestinationFilter
        id="source-page"
        feature={DestinationFeature.HISTORY}
        required
        value={destination}
        allLabel={t('source.selectPagePlaceholder')}
        onChange={setDestination}
      />

      {socialId !== '' && <SocialPosts accountId={socialId} />}

      {posts.isLoading && pageId && <div className="loading">{t('source.loadingPosts')}</div>}
      {posts.isError && <ErrorNotice error={posts.error} />}

      {socialId === '' && (
        <PostList
          posts={posts.data?.posts ?? []}
          onSelect={(post) =>
            onSelect({
              sourceKind: 'OWNED_PAGE_POST',
              sourcePostId: post.id,
              label: post.externalId,
              preview: post.message?.slice(0, 120) ?? t('common.noText'),
            })
          }
        />
      )}
    </>
  );
}

/**
 * A social account's posts, with editing and deleting where the network allows.
 * Not a source to republish from: no copy of the post is kept here to send again.
 */
function SocialPosts({ accountId }: { accountId: string }) {
  const { t } = useI18n();
  const fmt = useFormatters();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const posts = useQuery({
    queryKey: ['social-posts', accountId],
    queryFn: () => api.socialAccountPosts(accountId),
  });

  const done = (text: string) => {
    setNotice({ kind: 'ok', text });
    setEditing(null);
    void queryClient.invalidateQueries({ queryKey: ['social-posts', accountId] });
    void queryClient.invalidateQueries({ queryKey: ['published-posts'] });
  };
  const failed = (error: unknown) =>
    setNotice({
      kind: 'error',
      text: error instanceof ApiError ? error.message : t('common.requestFailed'),
    });

  const save = useMutation({
    mutationFn: (args: { postId: string; text: string }) =>
      api.editSocialPost(accountId, args.postId, args.text),
    onSuccess: () => done(t('source.saved')),
    onError: failed,
  });
  const remove = useMutation({
    mutationFn: (postId: string) => api.deleteSocialPost(accountId, postId),
    onSuccess: () => done(t('source.deleted')),
    onError: failed,
  });

  return (
    <>
      <div className="inline-form">
        <button
          type="button"
          className="ghost small"
          onClick={() => void posts.refetch()}
          disabled={posts.isFetching}
        >
          {t('source.refresh')}
        </button>
      </div>

      {posts.data && !posts.data.networkReadable && (
        <p className="muted small">{t('source.profileLimit')}</p>
      )}
      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}
      {posts.isLoading && <div className="loading">{t('source.loadingPosts')}</div>}
      {posts.isError && <ErrorNotice error={posts.error} />}
      {posts.data && posts.data.posts.length === 0 && (
        <p className="muted">{t('source.socialEmpty')}</p>
      )}

      <ul className="plain-list">
        {(posts.data?.posts ?? []).map((post) => (
          <li key={post.id} className="card">
            <span className="badge tiny">{t(`source.origin.${post.origin}` as TranslationKey)}</span>{' '}
            <span className="tiny muted">{fmt.dateTime(post.createdAt)}</span>

            {editing === post.id ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (draft.trim()) save.mutate({ postId: post.id, text: draft.trim() });
                }}
              >
                <textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  rows={4}
                  dir="auto"
                  maxLength={3000}
                />
                <div className="actions">
                  <button type="submit" className="primary small" disabled={save.isPending || !draft.trim()}>
                    {t('common.save')}
                  </button>
                  <button type="button" className="ghost small" onClick={() => setEditing(null)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            ) : post.text ? (
              <p className="published-text" dir="auto">
                {post.text}
              </p>
            ) : (
              <p className="tiny muted">{t('common.noText')}</p>
            )}

            {editing !== post.id && (
              <div className="actions">
                <a href={post.permalink} target="_blank" rel="noreferrer noopener">
                  {t('common.open')} <Icon.external />
                </a>
                <button
                  type="button"
                  className="ghost small"
                  onClick={() => {
                    setEditing(post.id);
                    setDraft(post.text ?? '');
                  }}
                >
                  {t('common.edit')}
                </button>
                <button
                  type="button"
                  className="danger small"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(t('source.confirmDeleteSocial'))) remove.mutate(post.id);
                  }}
                >
                  {t('common.delete')}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

function PublicSource({ onSelect }: { onSelect: (selection: Selection) => void }) {
  const [reference, setReference] = useState('');
  const [sourceId, setSourceId] = useState('');
  const { t } = useI18n();

  const sources = useQuery({
    queryKey: ['public-sources'],
    queryFn: api.publicSourcePages,
  });

  const add = useMutation({
    mutationFn: api.addPublicSourcePage,
    onSuccess: () => sources.refetch(),
  });

  const posts = useQuery({
    queryKey: ['public-posts', sourceId],
    queryFn: () => api.publicPosts(sourceId),
    enabled: sourceId !== '',
  });

  // The API refuses these calls entirely when the feature is not approved, and
  // the error text explains why. Showing it verbatim is more useful than a
  // generic failure state.
  if (sources.isError) return <ErrorNotice error={sources.error} />;

  return (
    <>
      <div className="inline-form">
        <input
          type="text"
          className="ltr"
          placeholder={t('source.publicPlaceholder')}
          value={reference}
          onChange={(event) => setReference(event.target.value)}
        />
        <button
          type="button"
          onClick={() => add.mutate(reference)}
          disabled={!reference || add.isPending}
        >
          <Icon.plus />
          {t('source.addPublic')}
        </button>
      </div>
      {add.isError && <ErrorNotice error={add.error} />}

      <label htmlFor="public-source">{t('source.registeredPublic')}</label>
      <select
        id="public-source"
        value={sourceId}
        onChange={(event) => setSourceId(event.target.value)}
      >
        <option value="">{t('source.selectPublicPlaceholder')}</option>
        {sources.data?.pages.map((page) => (
          <option key={page.id} value={page.id}>
            {withChannel(t, 'FACEBOOK', page.name)}
          </option>
        ))}
      </select>

      {posts.isError && <ErrorNotice error={posts.error} />}

      <PostList
        posts={posts.data?.posts ?? []}
        onSelect={(post) =>
          onSelect({
            sourceKind: 'PUBLIC_PAGE_POST',
            sourcePostId: post.id,
            label: post.externalId,
            preview: post.message?.slice(0, 120) ?? t('common.noText'),
          })
        }
      />
    </>
  );
}

function PostList({
  posts,
  onSelect,
}: {
  posts: SourcePostDto[];
  onSelect: (post: SourcePostDto) => void;
}) {
  const { t } = useI18n();
  if (posts.length === 0) return null;

  return (
    <>
      <p className="muted small" style={{ marginTop: 16 }}>
        {t('source.postCount', { count: posts.length })}
      </p>
      <div className="post-grid">
        {posts.map((post) => (
          <PostCard key={post.id} post={post} onSelect={onSelect} />
        ))}
      </div>
    </>
  );
}
